import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class TrafficResultsServiceService {
  private pmReportColumnMapping = {
    delayNtoF: ['FD 1way Min N->F', 'FD 1way Avg N->F', 'FD 1way Max N->F'],
    delayFtoN: ['FD 1way Min F->N', 'FD 1way Avg F->N', 'FD 1way Max F->N'],
    twoWayDelay: ['FD 2way Min', 'FD 2way Avg', 'FD 2way Max'],
    delayVariationNtoF: ['IFDV 1way Min N->F', 'IFDV 1way Avg N->F', 'IFDV 1way Max N->F'],
    delayVariationFtoN: ['IFDV 1way Min F->N', 'IFDV 1way Avg F->N', 'IFDV 1way Max F->N'],
    twoWayDelayVariation: ['IFDV 2way Min', 'IFDV 2way Avg', 'IFDV 2way Max'],
    framesNtoF: ['Frames Attempted 1way N->F', 'Frames Lost 1way N->F'],
    framesFtoN: ['Frames Attempted 1way F->N', 'Frames Lost 1way F->N']
  };

  private jmepModel = 'JMEP-1G';

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  setJMEPModel(jmepObj: any): void {
    this.jmepModel = jmepObj.model && jmepObj.model.model ? jmepObj.model.model : this.jmepModel;
  }

  getJMEPModel(): string {
    return this.jmepModel;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getTableSummaryObject(item: any): any {
    const calculateStartTime = new Date(item['Monitor Period End Time']);
    return {
      periodStartTime: item['Monitor Period End Time']
        ? new Date(calculateStartTime.setMinutes(calculateStartTime.getMinutes() - 5))
        : '',
      granularity: this.getGranularity(item.Granularity),
      monitorName: item['Monitor Name'],
      periodStatus: item['Period Status'],
      monitorPeriodEndTime: item['Monitor Period End Time']
        ? new Date(item['Monitor Period End Time']).getTime()
        : '',
      monitoredTime: this.getTimeinMin(item['Monitored Time']),
      timeUnsync: this.getTimeUnsync(item['Time Unsync']),
      sampleInterval: this.getTrafficInterval(item['Sample Interval'])
    };
  }

  getTimeUnsync(value: string): string {
    const timeUnsync = parseInt(value);
    return timeUnsync === 1 ? 'true' : timeUnsync === 0 ? 'false' : 'invalid';
  }

  getGranularity(value: string): string {
    return value.includes('-') ? value.replace('-', ' ') : value;
  }

  getTrafficInterval(interval: string): string {
    const trafficInterval = parseInt(interval);
    return trafficInterval === 0
      ? 'disabled'
      : trafficInterval
      ? `${trafficInterval} ms`
      : interval;
  }

  getTimeinMin(value: string): string {
    return parseInt(value) / (1000000 * 60) + ' min';
  }
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getMajorMinorVersion(version): any {
    let majorMinorVersion = version;
    if (typeof version === 'string') {
      const versionParts = version.split('.');
      if (versionParts && versionParts.length >= 2) {
        majorMinorVersion = versionParts[0] + '.' + versionParts[1];
      }
    }
    return majorMinorVersion;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  isJmep10versionOne(version): any {
    const jmep10gVersionOne = ['10.0', '10.1'];
    const majorMinorVersion = this.getMajorMinorVersion(version);
    return jmep10gVersionOne.indexOf(majorMinorVersion) !== -1;
  }

  transformSummaryData(data: any[]): any {
    const template = data.map(item => this.getTableSummaryObject(item));
    return template;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getInterfaceColumn(item: any, prefix: string, columnName: string): any {
    return (
      item[`${prefix} ${columnName}`] || item[`${this.getFullPrefixName(prefix)} ${columnName}`]
    );
  }

  getFullPrefixName(prefix: string): string {
    if (prefix === 'In') return 'Ingress';
    if (prefix === 'Eg') return 'Egress';
    return prefix;
  }

  getInterfaceName(prefix: string): string {
    const interfaceNames = { In: 'Ingress', Eg: 'Egress', Eqp: 'Equipment', Net: 'Network' };
    return interfaceNames[prefix] || prefix;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getTableInterfaceObject(item: any, prefix: string): any {
    return {
      interface: this.getInterfaceName(prefix),
      octets: this.getInterfaceColumn(item, prefix, 'Octets'),
      frames: this.getInterfaceColumn(item, prefix, 'Frames'),
      excludedOctets: this.getInterfaceColumn(item, prefix, 'Excluded Octets'),
      excludedFrames: this.getInterfaceColumn(item, prefix, 'Excluded Frames'),
      peakOctets: this.getInterfaceColumn(item, prefix, 'Peak Octets'),
      bin1: this.getInterfaceColumn(item, prefix, 'Bin 1'),
      bin2: this.getInterfaceColumn(item, prefix, 'Bin 2'),
      bin3: this.getInterfaceColumn(item, prefix, 'Bin 3'),
      bin4: this.getInterfaceColumn(item, prefix, 'Bin 4'),
      bin5: this.getInterfaceColumn(item, prefix, 'Bin 5'),
      bin6: this.getInterfaceColumn(item, prefix, 'Bin 6'),
      bin7: this.getInterfaceColumn(item, prefix, 'Bin 7'),
      bin8: this.getInterfaceColumn(item, prefix, 'Bin 8'),
      bin9: this.getInterfaceColumn(item, prefix, 'Bin 9'),
      bin10: this.getInterfaceColumn(item, prefix, 'Bin 10'),
      bin11: this.getInterfaceColumn(item, prefix, 'Bin 11'),
      bin12: this.getInterfaceColumn(item, prefix, 'Bin 12'),
      bin13: this.getInterfaceColumn(item, prefix, 'Bin 13'),
      bin14: this.getInterfaceColumn(item, prefix, 'Bin 14'),
      bin15: this.getInterfaceColumn(item, prefix, 'Bin 15'),
      bin16: this.getInterfaceColumn(item, prefix, 'Bin 16'),
      bin17: this.getInterfaceColumn(item, prefix, 'Bin 17'),
      bin18: this.getInterfaceColumn(item, prefix, 'Bin 18'),
      bin19: this.getInterfaceColumn(item, prefix, 'Bin 19')
    };
  }

  transformInterfaceData(data: any[], prefixNames: string[]): any {
    const template = prefixNames.reduce((temp, prefix) => {
      return temp.concat(data.map(item => this.getTableInterfaceObject(item, prefix)));
    }, []);
    return template;
  }

  getBinTitle(value: number): string {
    const multiplier = this.getJMEPModel() === 'JMEP-10G' ? 10 : 1;
    return `${multiplier * value}+ Mbps`;
  }

  transformPMData(data: any[]): any {
    const template = data.map(item => this.getTablePMReportObject(item));
    // template.data = temp;
    return template;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getMinMaxAvgValues(item: any, columns: string[]): any[] {
    return columns.map((column, index) => ({
      type: index === 0 ? 'min' : index === 1 ? 'avg' : 'max',
      label: item[column]
    }));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getFramesValues(item: any, columns: string[]): any[] {
    return columns.map((column, index) => ({
      type: index === 0 ? 'attempted' : 'lost',
      label: item[column]
    }));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getTablePMReportObject(item: any): any {
    return {
      delayNtoF: this.getMinMaxAvgValues(item, this.pmReportColumnMapping.delayNtoF),
      delayFtoN: this.getMinMaxAvgValues(item, this.pmReportColumnMapping.delayFtoN),
      twoWayDelay: this.getMinMaxAvgValues(item, this.pmReportColumnMapping.twoWayDelay),
      delayVariationNtoF: this.getMinMaxAvgValues(
        item,
        this.pmReportColumnMapping.delayVariationNtoF
      ),
      delayVariationFtoN: this.getMinMaxAvgValues(
        item,
        this.pmReportColumnMapping.delayVariationFtoN
      ),
      twoWayDelayVariation: this.getMinMaxAvgValues(
        item,
        this.pmReportColumnMapping.twoWayDelayVariation
      ),
      framesNtoF: this.getFramesValues(item, this.pmReportColumnMapping.framesNtoF),
      framesFtoN: this.getFramesValues(item, this.pmReportColumnMapping.framesFtoN)
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  formatCount(num: any): string {
    if (isNaN(num) || num < 1) return num;
    const sizeUnits = ['', 'k', 'M', 'G', 'T'];
    const parsedNum = parseInt(num, 10);
    const index = Math.floor(Math.log(parsedNum) / Math.log(1000));
    return (
      (Math.round((parsedNum / Math.pow(1000, index)) * 10) / 10).toString() + sizeUnits[index]
    );
  }

  formatData(data: any[]): any[] {
    return data.map(item => {
      const formattedItem = { ...item, originalValues: {} };
      Object.keys(item).forEach(key => {
        if (key !== 'interface') {
          // Store original value in `originalValues`
          formattedItem.originalValues[key] = item[key];
          // Format value for display
          formattedItem[key] = this.formatCount(item[key]);
        }
      });
      return formattedItem;
    });
  }
}
